import {
  MutableRefObject,
  RefCallback,
  RefObject,
  useCallback,
  useRef,
} from 'react';

type THandler = () => void;

export default function useOnClickOutside<Type extends HTMLElement>(
  handler: THandler,
  ref: MutableRefObject<Type | null>,
): RefCallback<Type> {
  const listener = useCallback(
    ({ target }: Event) => {
      if (
        target instanceof HTMLElement &&
        ref.current &&
        !ref.current?.contains(target)
      ) {
        handler();
      }
    },
    [handler, ref],
  );

  return useCallback(
    (node) => {
      if (ref.current || !node) {
        // cleanup of any events/references added to the last instance
        document.removeEventListener('click', listener);
      }

      ref.current = node;

      if (node) {
        // it prevents registering event that opens the component
        requestAnimationFrame(() =>
          document.addEventListener('click', listener),
        );
      }
    },
    [listener, ref],
  );
}

export function useOnClickOutsideWithRef<Type extends HTMLElement>(
  handler: THandler,
): [RefCallback<Type>, RefObject<Type>] {
  const ref = useRef<Type | null>(null);

  return [useOnClickOutside(handler, ref), ref];
}
